import { unwrap } from "@sagot/api-client";
import { CHECKOUT_CONFIRM_MAX_POLLS, CHECKOUT_CONFIRM_POLL_MS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { toast } from "@sagot/ui";
import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useApi } from "../../../session/session-provider";
import type { PaymentStatusOut } from "../shared";
import { useInvalidateBilling } from "./use-billing";

const PENDING_TOAST_ID = "billing-checkout-confirm";

/**
 * Back from the gateway's cancel URL: the abandoned checkout's order invoice is voided on the
 * server (it is not left open to be dunned), then the billing queries refresh. A failure is
 * harmless: the hourly sweep voids it once the checkout has expired.
 */
export function useCancelPayment(): UseMutationResult<PaymentStatusOut, Error, string> {
  const api = useApi();
  const invalidate = useInvalidateBilling();
  return useMutation<PaymentStatusOut, Error, string>({
    mutationFn: (paymentId) => unwrap(api.POST("/v1/billing/payments/{payment_id}/cancel", { params: { path: { payment_id: paymentId } } })),
    onSettled: async () => { await invalidate(); },
  });
}

/**
 * Back from the gateway: settle now instead of waiting for the webhook (idempotent). PayMongo
 * settles by webhook only, so a pending answer is asked again every few seconds, refreshing the
 * billing queries each time, until the payment lands: the page really "updates once it goes
 * through" instead of staying on the plan it had before paying.
 */
export function useConfirmPayment(): UseMutationResult<PaymentStatusOut, Error, string> {
  const { t } = useTranslation();
  const api = useApi();
  const invalidate = useInvalidateBilling();
  const [waitingFor, setWaitingFor] = useState<string | null>(null);
  const confirm = useMutation<PaymentStatusOut, Error, string>({
    mutationFn: (paymentId) => unwrap(api.POST("/v1/billing/payments/{payment_id}/confirm", { params: { path: { payment_id: paymentId } } })),
    onSuccess: async (result, paymentId) => {
      setWaitingFor(result.status === "pending" ? paymentId : null);
      if (result.status === "succeeded" || result.status === "failed") toast.dismiss(PENDING_TOAST_ID);
      if (result.status === "succeeded") toast.success(t("billing.checkout.returnSuccess"));
      else if (result.status === "failed") toast.warning(t("billing.checkout.failed"));
      else toast.info(t("billing.checkout.confirmPending"), { id: PENDING_TOAST_ID });
      await invalidate();
    },
    onError: (_error, paymentId) => {
      setWaitingFor(paymentId);
      toast.info(t("billing.checkout.confirmPending"), { id: PENDING_TOAST_ID });
    },
  });
  const { mutate } = confirm;
  useEffect(() => {
    if (!waitingFor) return undefined;
    let polls = 0;
    const timer = setInterval(() => {
      polls += 1;
      if (polls > CHECKOUT_CONFIRM_MAX_POLLS) {
        clearInterval(timer);
        setWaitingFor(null);
        return;
      }
      mutate(waitingFor);
    }, CHECKOUT_CONFIRM_POLL_MS);
    return () => { clearInterval(timer); };
  }, [waitingFor, mutate]);
  return confirm;
}
